import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import BrandMark from "@/components/BrandMark";

export default function SignupPage() {
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", requested_role: "customer", reason: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/signup", f);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#050505]">
        <div className="card-lux p-10 max-w-md text-center fade-up">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-display text-3xl">Request submitted</h1>
          <p className="text-zinc-400 mt-3">Thank you, <span className="text-[#D4AF37]">{f.name}</span>. An administrator will review your request and notify you by phone or email once approved.</p>
          <Link to="/login" className="mt-6 inline-block text-sm text-zinc-500 hover:text-[#D4AF37]">← Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#050505]">
      <div className="w-full max-w-md fade-up">
        <Link to="/login" className="text-sm text-zinc-500 hover:text-[#D4AF37] inline-flex items-center gap-1.5 mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <BrandMark size={40} eyebrow="Request Access" />
        </div>

        <h2 className="font-display text-4xl mb-2">Join the brigade.</h2>
        <p className="text-zinc-500 text-sm mb-8">Apply for an account. An admin will review your request and assign you a role.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <Field label="Full name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="su-name" className="bg-[#0a0a0a] border-[#2a2a2a]" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="su-email" className="bg-[#0a0a0a] border-[#2a2a2a]" /></Field>
          <Field label="Password"><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="su-password" className="bg-[#0a0a0a] border-[#2a2a2a]" /></Field>
          <Field label="Phone (optional)"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-[#0a0a0a] border-[#2a2a2a]" /></Field>
          <Field label="I'd like to join as">
            <select value={f.requested_role} onChange={(e) => setF({ ...f, requested_role: e.target.value })} data-testid="su-role"
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm capitalize">
              <option value="customer">Customer</option>
              <option value="operations">Operations team</option>
              <option value="kitchen">Kitchen team</option>
              <option value="driver">Driver</option>
              <option value="setup">Setup team</option>
            </select>
          </Field>
          <Field label="Why should we approve you? (optional)">
            <textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={3} data-testid="su-reason"
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-sm p-3 text-sm focus:outline-none focus:border-[#D4AF37]" />
          </Field>

          <Button type="submit" disabled={loading} data-testid="su-submit" className="w-full h-11 bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow text-[10px]">{label}</Label>
      {children}
    </div>
  );
}
